// src/services/api.js
import axios from 'axios';

export const API_BASE_URL = '/backend/api';
export const BACKEND_BASE = '/backend';


// Interceptor para añadir el token a todas las peticiones
axios.interceptors.request.use(
    config => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    error => {
        return Promise.reject(error);
    }
);

// 🆕 Interceptor para manejar respuestas y errores 401
axios.interceptors.response.use(
    response => {
        return response;
    },
    error => {
        if (error.response && error.response.status === 401) {
            const requestUrl = error.config?.url || '';

            // No tratar como "sesión expirada" las rutas de autenticación
            // (login/register devuelven 401 por credenciales inválidas, no por sesión)
            const isAuthRoute = requestUrl.includes('/auth.php') ||
                                requestUrl.includes('/login') ||
                                requestUrl.includes('/register');

            // Solo disparar "sesión expirada" si había un token activo (sesión real)
            const hadToken = localStorage.getItem('token');

            if (!isAuthRoute && hadToken) {
                console.log('🚨 Error 401: Token expirado o inválido');

                localStorage.removeItem('token');
                localStorage.removeItem('user');

                window.dispatchEvent(new CustomEvent('sessionExpired', {
                    detail: { message: 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.' }
                }));

                setTimeout(() => {
                    window.location.href = '/login';
                }, 1500);
            }
        }

        return Promise.reject(error);
    }
);

export const certificadoService = {
    getCertificadosByUser: (userId) =>
      axios.get(`${API_BASE_URL}/certificados.php?user_id=${userId}`),
    misCertificados: () =>
      axios.get(`${API_BASE_URL}/certificados-v2.php?action=mis_certificados`),
    descargarPDF: (id) =>
      axios.get(`${API_BASE_URL}/certificados-v2.php?action=descargar&id=${id}`, { responseType: 'blob' }),
    verificar: (codigo) =>
      axios.get(`${API_BASE_URL}/certificados-v2.php?action=verificar&codigo=${codigo}`),
    verificarEmision: (proyectoId) =>
      axios.post(`${API_BASE_URL}/certificados-v2.php?action=verificar_emision`, { proyecto_id: proyectoId }),
    uploadBackground: (proyectoId, file) => {
      const formData = new FormData();
      formData.append('proyecto_id', proyectoId);
      formData.append('imagen', file);
      return axios.post(`${API_BASE_URL}/admin/certificado-upload.php`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    porProyecto: (proyectoId) =>
      axios.get(`${API_BASE_URL}/certificados-v2.php?action=por_proyecto&proyecto_id=${proyectoId}`),
    descargarAdmin: (id) =>
      axios.get(`${API_BASE_URL}/certificados-v2.php?action=descargar_admin&id=${id}`, { responseType: 'blob' }),
    descargarZip: (proyectoId) =>
      axios.get(`${API_BASE_URL}/certificados-v2.php?action=descargar_zip&proyecto_id=${proyectoId}`, { responseType: 'blob' }),
    emitirPendientes: (proyectoId) =>
      axios.post(`${API_BASE_URL}/certificados-v2.php?action=emitir_pendientes`, { proyecto_id: proyectoId })
  };
  

  export const adminService = {
    getDashboardStats: () => {
      return axios.get(`${API_BASE_URL}/admin/dashboard-stats.php`);
      },

      // Gestión de servicio de voz
    getVoiceService: () => {
      return axios.get(`${API_BASE_URL}/admin/voice-service.php`);
  },
  
  updateVoiceService: (config) => {
      return axios.post(`${API_BASE_URL}/admin/voice-service.php`, config);
  },
  
  getAvailableVoices: (service) => {
      return axios.get(`${API_BASE_URL}/voices.php?service=${service}`);
  },

  // Correcciones STT (Speech-to-Text)
  getSttCorrections: () => {
      return axios.get(`${API_BASE_URL}/admin/stt-corrections.php`);
  },
  updateSttCorrections: (corrections) => {
      return axios.post(`${API_BASE_URL}/admin/stt-corrections.php`, { corrections });
  }

  };
  

// Servicios de progreso del usuario
export const progressService = {
    getUserProgress: (userId) => {
        return axios.get(`${API_BASE_URL}/progress.php?user_id=${userId}`);
    },
    saveProgress: (progressData) => {
        return axios.post(`${API_BASE_URL}/progress.php`, progressData);
    },
    getLastActivity: (userId) => {
        return axios.get(`${API_BASE_URL}/progress.php?user_id=${userId}&last_activity=true`);
    },

    getFullCourseProgress: (userId, cursoId, moduleId) => {
        return axios.get(`${API_BASE_URL}/progress.php?user_id=${userId}&curso_id=${cursoId}&module_id=${moduleId}`);
      },

      registerCompletedCourse: ({ user_id, curso_id }) => {
        return axios.post(`${API_BASE_URL}/completed_course.php`, {
            user_id,
            curso_id
        });   
    },

      // Nuevos servicios para administradores
  getSystemProgress: () => {
    return axios.get(`${API_BASE_URL}/admin/progress-stats.php`);
  },
  
  getActivityByDay: () => {
    return axios.get(`${API_BASE_URL}/admin/activity-by-day.php`);
  },
  
  getUserRegistrationsByDate: () => {
    return axios.get(`${API_BASE_URL}/admin/user-registrations.php`);
  },
  
  getCourseCompletion: (courseId) => {
    return axios.get(`${API_BASE_URL}/admin/course-completion.php?course_id=${courseId}`);
  }
};

// Servicios de evaluación
export const evaluationService = {
    submitEvaluation: (evaluationData) => {
        return axios.post(`${API_BASE_URL}/evaluations.php`, evaluationData);
    },
    getUserEvaluations: (userId) => {
        return axios.get(`${API_BASE_URL}/evaluations.php?user_id=${userId}`);
    },
    getEvaluationById: (id) => {
        return axios.get(`${API_BASE_URL}/evaluations.php?id=${id}`);
    },
    evaluateAnswer: (question, expectedAnswer, userAnswer) => {
        return axios.post(`${API_BASE_URL}/evaluate-answer.php`, {
            question,
            expectedAnswer,
            userAnswer
        });
    },

    // Nuevos servicios para administradores
  getAllEvaluations: () => {
    return axios.get(`${API_BASE_URL}/admin/evaluations.php`);
  },
  getEvaluationWithQuestions: (id) => {
    return axios.get(`${API_BASE_URL}/admin/evaluations.php?id=${id}&include_questions=true`);
  },
  createEvaluation: (evaluationData) => {
    return axios.post(`${API_BASE_URL}/admin/evaluations.php`, evaluationData);
  },
  updateEvaluation: (evaluationData) => {
    return axios.put(`${API_BASE_URL}/admin/evaluations.php`, evaluationData);
  },
  deleteEvaluation: (id) => {
    return axios.delete(`${API_BASE_URL}/admin/evaluations.php`, { 
      data: { id } 
    });
  },
  getScoreDistribution: () => {
    return axios.get(`${API_BASE_URL}/admin/evaluation-scores.php`);
  }

};

// Servicios de preguntas
export const questionService = {
    getQuestionsByModule: (moduleId) => {
        return axios.get(`${API_BASE_URL}/questions.php?module_id=${moduleId}`);
    }
};

// Servicios de autenticación
export const authService = {
    login: (credentials) => {
        return axios.post(`${API_BASE_URL}/auth.php?action=login`, credentials);
    },
    register: (userData) => {
        return axios.post(`${API_BASE_URL}/auth.php?action=register`, userData);
    },
    logout: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
    }
};

// Servicios de cursos
export const courseService = {
    getAllCourses: () => {
        return axios.get(`${API_BASE_URL}/courses.php`);
    },
    getCourseById: (id) => {
        return axios.get(`${API_BASE_URL}/courses.php?id=${id}`);
    },
    getModulesByCourse: (courseId) => {
        return axios.get(`${API_BASE_URL}/modules.php?course_id=${courseId}`);
    },

    // Nuevos servicios para administradores
  createCourse: (courseData) => {
    return axios.post(`${API_BASE_URL}/admin/courses.php`, courseData);
  },
  updateCourse: (courseData) => {
    return axios.put(`${API_BASE_URL}/admin/courses.php`, courseData);
  },
  deleteCourse: (id) => {
    return axios.delete(`${API_BASE_URL}/admin/courses.php`, { 
      data: { id } 
    });
  }

};

// Servicios de lecciones
export const lessonService = {
    getLessonsByCourse: (courseId) => {
        return axios.get(`${API_BASE_URL}/lessons.php?curso_id=${courseId}`);
    },
    getLessonById: (id) => {
        return axios.get(`${API_BASE_URL}/lessons.php?id=${id}`);
    },
    getLessonsByModule: (moduleId) => {
        return axios.get(`${API_BASE_URL}/lessons.php?module_id=${moduleId}`);
    },
    getNextActivity: (moduleId, currentLessonId) => {
        return axios.get(`${API_BASE_URL}/lessons.php?action=next_activity&module_id=${moduleId}&current_lesson_id=${currentLessonId}`);
      },
      
      
      markLessonCompleted: ({ user_id, lesson_id, completado = 1 }) => {
        // Si no hay user_id, intentar recuperarlo del localStorage
        if (!user_id) {
          try {
            const storedUser = JSON.parse(localStorage.getItem('user'));
            if (storedUser && storedUser.id) {
              console.log('Recuperando ID de usuario desde localStorage en api.js:', storedUser.id);
              user_id = storedUser.id;
            }
          } catch (e) {
            console.error('Error recuperando usuario desde localStorage en api.js:', e);
          }
        }
      
        // Convertir a números
        const userData = {
          user_id: user_id ? (typeof user_id === 'string' ? parseInt(user_id, 10) : user_id) : null,
          lesson_id: typeof lesson_id === 'string' ? parseInt(lesson_id, 10) : lesson_id,
          completado: typeof completado === 'string' ? parseInt(completado, 10) : completado
        };
      
        // Registrar datos enviados
        console.log('Enviando datos a progress.php:', userData);
      
        return axios.post(`${API_BASE_URL}/progress.php`, userData)
          .then(response => {
            console.log('Respuesta de progress.php:', response.data);
            return response;
          })
          .catch(error => {
            console.error('Error al marcar lección completada:', error);
            if (error.response) {
              console.error('Datos de respuesta del error:', error.response.data);
            }
            throw error;
          });
      }
      
};

// Servicios de voz
export const voiceService = {
    // Texto a voz
    speak: (text, options = {}) => {

          // 🔍 AGREGAR ESTAS LÍNEAS AQUÍ
    console.log('🔍 DEBUG API - options completo:', options);
    console.log('🔍 DEBUG API - options.sessionToken:', options.sessionToken);
    console.log('🔍 DEBUG API - typeof:', typeof options.sessionToken);

        return axios.post(`${API_BASE_URL}/tts.php`, { 
            text,
            forceService: options.forceService,
            voiceId: options.voiceId,
            sessionToken: options.sessionToken // 🆕 AGREGAR ESTA LÍNEA
        });
    },
    
    // Obtener voces disponibles
    getVoices: (service = 'polly') => {
        return axios.get(`${API_BASE_URL}/voices.php?service=${service}`);
    },
    
    // Interpretación de comandos con OpenAI
    interpretCommand: (command, context = {}) => {
        return axios.post(`${API_BASE_URL}/voice-command.php`, { command, context });
    }
};

export const completedCourseService = {
    saveCompletedCourse: (userId, cursoId) => {
      return axios.post(`${API_BASE_URL}/completed.php`, {
        user_id: userId,
        curso_id: cursoId
      });
    }
  };
  
  export const userService = {
    getAll: () => axios.get(`${API_BASE_URL}/users.php`),
    deleteUser: (id) => axios.delete(`${API_BASE_URL}/users.php`, { data: { id } }),
    updateUser: (data) => axios.put(`${API_BASE_URL}/users.php`, data),
    updateIdioma: (idioma) => axios.patch(`${API_BASE_URL}/users.php`, { idioma_preferido: idioma }),

    // Nuevos servicios para administradores
  createUser: (userData) => {
    return axios.post(`${API_BASE_URL}/admin/create-user.php`, userData);
  }

  };
 
  // src/services/api.js
// Agregar al final del archivo:

// Servicios de consulta
export const consultaService = {
  // Obtener información de un documento
  getDocumentById: (documentId) => {
    return axios.get(`${API_BASE_URL}/documentos.php?id=${documentId}`);
  },
  
  // Enviar una consulta
  sendQuestion: (data) => {
    return axios.post(`${API_BASE_URL}/consulta.php`, data);
  },
  
  // Obtener lista de documentos disponibles
  getDocumentos: () => {
    return axios.get(`${API_BASE_URL}/documentos.php`);
  },
  
  // Crear un nuevo documento
  createDocumento: (formData) => {
    console.log("Enviando createDocumento a", `${API_BASE_URL}/documentos.php`);
    
    // Debug - listar contenido del FormData
    for (let pair of formData.entries()) {
      console.log(pair[0] + ': ' + (pair[0] === 'imagen' ? 'File object' : pair[1]));
    }
    
    return axios.post(`${API_BASE_URL}/documentos.php`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  // Actualizar un documento existente
// Actualizar un documento existente
updateDocumento: (data) => {
  console.log('🔄 updateDocumento - Enviando PUT con datos:', data);
  return axios.put(`${API_BASE_URL}/documentos.php`, data, {
    headers: {
      'Content-Type': 'application/json'
    }
  });
},
  
  // Subir/actualizar imagen de un documento
  uploadDocumentoImagen: (documentId, file) => {
    const formData = new FormData();
    formData.append('imagen', file);
    return axios.patch(`${API_BASE_URL}/documentos.php?id=${documentId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Subir/actualizar logo de un documento
  uploadDocumentoLogo: (documentId, file) => {
    const formData = new FormData();
    formData.append('logo', file);
    return axios.patch(`${API_BASE_URL}/documentos.php?id=${documentId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Eliminar un documento
  deleteDocumento: (id) => {
    return axios.delete(`${API_BASE_URL}/documentos.php?id=${id}`);
  },
  
  // Obtener progreso del modo mentor para un usuario
  getMentorProgress: (documentId, userId) => {
    return axios.get(`${API_BASE_URL}/mentor/progreso.php`, {
      params: {
        document_id: documentId,
        user_id: userId
      }
    });
  },

  // Obtener video de una lección específica
  getVideoForLesson: (documentId, moduleNumber, lessonNumber) => {
    return axios.get(`${API_BASE_URL}/mentor/video-leccion.php`, {
      params: {
        document_id: documentId,
        modulo: moduleNumber,
        leccion: lessonNumber
      }
    });
  }
};

// Servicios de anexos multimedia
export const attachmentService = {
  // Subir anexo
upload: (documentId, file, titulo, descripcion = '', keywords = '', transcripcion = '') => {
  const formData = new FormData();
  
  if (file) {
    formData.append('file', file);
  }
  formData.append('document_id', documentId);
  formData.append('titulo', titulo);
  formData.append('descripcion', descripcion);
  formData.append('keywords', keywords);
  formData.append('transcripcion', transcripcion); // ✅ AGREGAR ESTA LÍNEA

  return axios.post(`${API_BASE_URL}/anexos.php?action=upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress: (progressEvent) => {
      const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      window.dispatchEvent(new CustomEvent('uploadProgress', { 
        detail: { progress, file: file ? file.name : 'video-vimeo' } 
      }));
    }
  });
},



  // Obtener anexos por documento
  getByDocument: (documentId, activeOnly = true) => {
    return axios.get(`${API_BASE_URL}/anexos.php?action=by_document&document_id=${documentId}&active_only=${activeOnly}`);
  },

  // Obtener anexo específico
  getById: (anexoId) => {
    return axios.get(`${API_BASE_URL}/anexos.php?action=by_id&id=${anexoId}`);
  },

  // Buscar anexos
  search: (documentId, keywords) => {
    return axios.get(`${API_BASE_URL}/anexos.php?action=search&document_id=${documentId}&keywords=${encodeURIComponent(keywords)}`);
  },

  // Actualizar anexo
  update: (anexoData) => {
    return axios.put(`${API_BASE_URL}/anexos.php`, anexoData);
  },

  // Eliminar anexo
  delete: (anexoId, hardDelete = false) => {
    return axios.delete(`${API_BASE_URL}/anexos.php?id=${anexoId}&hard=${hardDelete}`);
  },

  // Obtener estadísticas
  getStats: () => {
    return axios.get(`${API_BASE_URL}/anexos.php?action=stats`);
  },

  // Limpiar archivos huérfanos
  cleanup: () => {
    return axios.post(`${API_BASE_URL}/anexos.php?action=cleanup`);
  },

  // Descargar archivo
  download: (anexoId, isThumb = false) => {
    const url = `${API_BASE_URL}/anexos.php?action=download&id=${anexoId}&thumb=${isThumb}`;
    window.open(url, '_blank');
  },

  // Validar archivo antes de subir
  validateFile: (file, maxSizeMB = 5) => {
    const errors = [];
    
    // Validar tamaño
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      errors.push(`El archivo es demasiado grande. Máximo: ${maxSizeMB}MB`);
    }

    // Validar tipo
    const allowedTypes = {
      image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      document: ['application/pdf'],
      video: ['video/mp4', 'video/webm'],
      audio: ['audio/mpeg', 'audio/wav', 'audio/ogg']
    };

    const isValidType = Object.values(allowedTypes).flat().includes(file.type);
    if (!isValidType) {
      errors.push('Tipo de archivo no permitido');
    }

    return {
      isValid: errors.length === 0,
      errors: errors,
      fileType: Object.keys(allowedTypes).find(type => 
        allowedTypes[type].includes(file.type)
      ) || 'other'
    };
  }
};

// Servicios de transcripciones de video
export const transcriptionService = {
  // Guardar transcripción
  save: (videoId, transcripcion, formato = 'texto', timestamps = null) => {
    return axios.post(`${API_BASE_URL}/video-transcripciones.php?action=save`, {
      video_id: videoId,
      transcripcion: transcripcion,
      formato: formato,
      timestamps: timestamps
    });
  },

  // Obtener transcripción
  get: (videoId) => {
    return axios.get(`${API_BASE_URL}/video-transcripciones.php?video_id=${videoId}`);
  },

  // Procesar transcripción de texto con timestamps
  processTimestampText: (text) => {
    const lines = text.split('\n');
    const processed = {
      texto_limpio: '',
      timestamps: []
    };

    lines.forEach(line => {
      const match = line.match(/^(\d+:\d+:\d+\.\d+),(\d+:\d+:\d+\.\d+)(.+)$/);
      if (match) {
        const [, inicio, fin, texto] = match;
        processed.texto_limpio += texto.trim() + ' ';
        processed.timestamps.push({
          inicio: inicio,
          fin: fin,
          texto: texto.trim()
        });
      }
    });

    return processed;
  }
};

// Servicios de reto semanal
export const retoService = {
  // Verificar si hay reto pendiente para el usuario
  verificarRetoPendiente: (documentId, userId) => {
    return axios.get(`${API_BASE_URL}/reto.php`, {
      params: {
        action: 'verificar',
        document_id: documentId,
        user_id: userId
      }
    });
  },

  // Obtener historial de retos del usuario
  obtenerHistorial: (documentId, userId, limite = 10) => {
    return axios.get(`${API_BASE_URL}/reto.php`, {
      params: {
        action: 'historial',
        document_id: documentId,
        user_id: userId,
        limite: limite
      }
    });
  },

  // Enviar respuesta a un reto
  responderReto: (retoId, userId, respuesta) => {
    return axios.post(`${API_BASE_URL}/reto.php`, {
      action: 'responder',
      reto_id: retoId,
      user_id: userId,
      respuesta: respuesta
    });
  },

  // Obtener estadísticas de retos para admin
  obtenerEstadisticasAdmin: (documentId) => {
    return axios.get(`${API_BASE_URL}/reto.php`, {
      params: {
        action: 'admin_stats',
        document_id: documentId
      }
    });
  },

  // Generar reto manualmente (admin)
  generarReto: (documentId, diaReto = null) => {
    return axios.post(`${API_BASE_URL}/reto.php`, {
      action: 'generar',
      document_id: documentId,
      dia_reto: diaReto
    });
  },

  // Obtener detalle de un reto específico con todas sus respuestas
  obtenerDetalleReto: (retoId) => {
    return axios.get(`${API_BASE_URL}/reto.php`, {
      params: {
        action: 'reto_detalle',
        reto_id: retoId
      }
    });
  }
};

// Servicio de notificaciones
export const notificacionService = {
  // Admin
  getAll: () => axios.get(`${API_BASE_URL}/admin/notificaciones.php`),
  create: (data) => axios.post(`${API_BASE_URL}/admin/notificaciones.php`, data),
  update: (data) => axios.put(`${API_BASE_URL}/admin/notificaciones.php`, data),
  delete: (id) => axios.delete(`${API_BASE_URL}/admin/notificaciones.php?id=${id}`),
  // Usuario
  getPendientes: () => axios.get(`${API_BASE_URL}/notificaciones.php`),
  marcarLeida: (notificacionId) => axios.post(`${API_BASE_URL}/notificaciones.php`, { notificacion_id: notificacionId }),
};

// Servicio de Seguimiento (Cohortes, Contactos, Matrículas)
export const seguimientoService = {
  // Cohortes
  getCohortes: () => axios.get(`${API_BASE_URL}/admin/cohortes.php`),
  getCohorte: (id) => axios.get(`${API_BASE_URL}/admin/cohortes.php?id=${id}`),
  createCohorte: (data) => axios.post(`${API_BASE_URL}/admin/cohortes.php`, data),
  updateCohorte: (data) => axios.put(`${API_BASE_URL}/admin/cohortes.php`, data),
  deleteCohorte: (id) => axios.delete(`${API_BASE_URL}/admin/cohortes.php?id=${id}`),

  // Contactos
  getContactos: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axios.get(`${API_BASE_URL}/admin/contactos.php?${query}`);
  },
  createContacto: (data) => axios.post(`${API_BASE_URL}/admin/contactos.php`, data),
  updateContacto: (data) => axios.put(`${API_BASE_URL}/admin/contactos.php`, data),
  deleteContacto: (id) => axios.delete(`${API_BASE_URL}/admin/contactos.php?id=${id}`),
  importarContactos: (data) => axios.post(`${API_BASE_URL}/admin/contactos.php`, { importar: true, ...data }),

  // Matrículas
  getMatriculas: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axios.get(`${API_BASE_URL}/admin/matriculas.php?${query}`);
  },
  createMatricula: (data) => axios.post(`${API_BASE_URL}/admin/matriculas.php`, data),
  updateMatricula: (data) => axios.put(`${API_BASE_URL}/admin/matriculas.php`, data),
  deleteMatricula: (id) => axios.delete(`${API_BASE_URL}/admin/matriculas.php?id=${id}`),
  asignarMasivo: (data) => axios.post(`${API_BASE_URL}/admin/matriculas.php`, { asignar_masivo: true, ...data }),

  // Reglas de recordatorio
  getReglas: (cohorteId) => axios.get(`${API_BASE_URL}/admin/reglas-recordatorio.php?cohorte_id=${cohorteId}`),
  updateReglas: (data) => axios.put(`${API_BASE_URL}/admin/reglas-recordatorio.php`, data),

  // Plantillas
  getPlantillas: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axios.get(`${API_BASE_URL}/admin/plantillas-mensaje.php?${query}`);
  },
  createPlantilla: (data) => axios.post(`${API_BASE_URL}/admin/plantillas-mensaje.php`, data),
  updatePlantilla: (data) => axios.put(`${API_BASE_URL}/admin/plantillas-mensaje.php`, data),
  deletePlantilla: (id) => axios.delete(`${API_BASE_URL}/admin/plantillas-mensaje.php?id=${id}`),

  // Estadísticas
  getStats: (cohorteId) => axios.get(`${API_BASE_URL}/admin/seguimiento-stats.php${cohorteId ? '?cohorte_id=' + cohorteId : ''}`),

  // Documentos (para selector de cohortes)
  getDocumentos: () => axios.get(`${API_BASE_URL}/documentos.php`),
};

// Servicio Mentor 2.0 (LMS híbrido)
export const mentor2Service = {
  start: (documentId) =>
    axios.get(`${API_BASE_URL}/mentor/start.php`, { params: { document_id: documentId } }),

  advance: (documentId) =>
    axios.post(`${API_BASE_URL}/mentor/advance.php`, { document_id: documentId }),

  chat: ({ documentId, videoId, pregunta, currentTime }) =>
    axios.post(`${API_BASE_URL}/mentor/chat.php`, {
      document_id: documentId,
      video_id: videoId,
      pregunta,
      current_time: currentTime
    }),

  generateQuiz: (documentId, videoId) =>
    axios.post(`${API_BASE_URL}/mentor/quiz.php`, {
      action: 'generate',
      document_id: documentId,
      video_id: videoId
    }),

  evaluateQuiz: (documentId, videoId, numeroPregunta, respuesta) =>
    axios.post(`${API_BASE_URL}/mentor/quiz.php`, {
      action: 'evaluate',
      document_id: documentId,
      video_id: videoId,
      numero_pregunta: numeroPregunta,
      respuesta
    }),

  saveVideoProgress: (data) =>
    axios.post(`${API_BASE_URL}/video-mentor-progress.php`, data),

  getVideoForLesson: (documentId, moduleNumber, lessonNumber) =>
    axios.get(`${API_BASE_URL}/mentor/video-leccion.php`, {
      params: { document_id: documentId, modulo: moduleNumber, leccion: lessonNumber }
    }),
};

// Servicio de sesión Realtime (OpenAI Realtime API)
export const realtimeSessionService = {
  createSession: (documentId, mode = 'consulta') => {
    return axios.post(`${API_BASE_URL}/realtime-session.php`, {
      document_id: documentId,
      mode: mode
    });
  },
  saveTranscripts: (documentId, transcripts, durationSeconds, mode = 'consulta') => {
    return axios.post(`${API_BASE_URL}/save-realtime-transcripts.php`, {
      document_id: documentId,
      transcripts: transcripts,
      duration_seconds: durationSeconds,
      mode: mode
    });
  }
};

export const practicaService = {
  evaluate: (documentId, doctorId, transcript, durationSeconds) => {
    return axios.post(`${API_BASE_URL}/practica-evaluar.php`, {
      document_id: documentId,
      doctor_id: doctorId,
      transcript: transcript,
      duration_seconds: durationSeconds
    });
  }
};

export const convenioService = {
  list: (proyectoId) => axios.get(`${API_BASE_URL}/admin/convenios.php?proyecto_id=${proyectoId}`),
  listAll: () => axios.get(`${API_BASE_URL}/admin/convenios.php?all=1`),
  create: (data) => axios.post(`${API_BASE_URL}/admin/convenios.php`, data),
  update: (data) => axios.put(`${API_BASE_URL}/admin/convenios.php`, data),
  delete: (id) => axios.delete(`${API_BASE_URL}/admin/convenios.php?id=${id}`)
};

export const especialidadService = {
  list: () => axios.get(`${API_BASE_URL}/admin/especialidades.php`)
};

export const paisService = {
  list: () => axios.get(`${API_BASE_URL}/admin/paises.php`)
};

export const proyectoService = {
  list: () => axios.get(`${API_BASE_URL}/admin/proyectos.php`),
  get: (id) => axios.get(`${API_BASE_URL}/admin/proyectos.php?id=${id}`),
  create: (formData) => axios.post(`${API_BASE_URL}/admin/proyectos.php`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (formData) => {
    return axios.post(`${API_BASE_URL}/admin/proyectos.php`, formData, {
      headers: { 'Content-Type': 'multipart/form-data', 'X-HTTP-Method-Override': 'PUT' }
    });
  },
  updateJSON: (data) => axios.put(`${API_BASE_URL}/admin/proyectos.php`, data),
  delete: (id) => axios.delete(`${API_BASE_URL}/admin/proyectos.php?id=${id}`),
  getInfo: () => axios.get(`${API_BASE_URL}/proyecto-info.php`),
  saveLandingSections: (proyectoId, secciones) => {
    return axios.put(`${API_BASE_URL}/admin/proyectos.php`, {
      id: proyectoId,
      config_json: { landing_secciones: secciones }
    });
  },
  uploadLandingImage: (proyectoId, seccionId, file) => {
    const formData = new FormData();
    formData.append('proyecto_id', proyectoId);
    formData.append('seccion_id', seccionId);
    formData.append('imagen', file);
    return axios.post(`${API_BASE_URL}/admin/landing-upload.php`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Miembros
  getMiembros: (proyectoId) =>
    axios.get(`${API_BASE_URL}/admin/proyectos.php?action=miembros&proyecto_id=${proyectoId}`),
  addMiembro: (data) =>
    axios.post(`${API_BASE_URL}/admin/proyectos.php?action=miembros`, data),
  removeMiembro: (id) =>
    axios.delete(`${API_BASE_URL}/admin/proyectos.php?action=miembros&id=${id}`),
  buscarUsuarios: (query) =>
    axios.get(`${API_BASE_URL}/admin/proyectos.php?action=buscar_usuarios&q=${encodeURIComponent(query)}`),
};

export const operatixService = {
  connectWhatsApp: (proyectoId, code) =>
    axios.post(`${API_BASE_URL}/operatix/connect-whatsapp.php`, { proyecto_id: proyectoId, code }),
  whatsappStatus: (proyectoId) =>
    axios.get(`${API_BASE_URL}/operatix/whatsapp-status.php?proyecto_id=${proyectoId}`),
  disconnectWhatsApp: (proyectoId) =>
    axios.post(`${API_BASE_URL}/operatix/disconnect-whatsapp.php`, { proyecto_id: proyectoId }),
  testConnection: () =>
    axios.get(`${API_BASE_URL}/operatix/test-connection.php`),
};

// ============ WA Training Service (Fase 11.4) ============
export const waTrainingService = {
  // Programas
  getProgramas: (proyectoId) =>
    axios.get(`${API_BASE_URL}/admin/wa-programas.php${proyectoId ? '?proyecto_id=' + proyectoId : ''}`),
  getPrograma: (id) =>
    axios.get(`${API_BASE_URL}/admin/wa-programas.php?id=${id}`),
  createPrograma: (data) =>
    axios.post(`${API_BASE_URL}/admin/wa-programas.php`, data),
  updatePrograma: (id, data) =>
    axios.put(`${API_BASE_URL}/admin/wa-programas.php`, { id, ...data }),
  deletePrograma: (id, hard = false) =>
    axios.delete(`${API_BASE_URL}/admin/wa-programas.php?id=${id}${hard ? '&hard=1' : ''}`),

  // Entregas
  getEntregas: (programaId) =>
    axios.get(`${API_BASE_URL}/admin/wa-entregas.php?programa_id=${programaId}`),
  getEntrega: (id) =>
    axios.get(`${API_BASE_URL}/admin/wa-entregas.php?id=${id}`),
  createEntrega: (data) =>
    axios.post(`${API_BASE_URL}/admin/wa-entregas.php`, data),
  updateEntrega: (id, data) =>
    axios.put(`${API_BASE_URL}/admin/wa-entregas.php`, { id, ...data }),
  reorderEntregas: (programaId, orden) =>
    axios.put(`${API_BASE_URL}/admin/wa-entregas.php`, { action: 'reorder', programa_id: programaId, orden }),
  deleteEntrega: (id) =>
    axios.delete(`${API_BASE_URL}/admin/wa-entregas.php?id=${id}`),

  // Inscripciones
  getInscripciones: (programaId) =>
    axios.get(`${API_BASE_URL}/admin/wa-inscripciones.php?programa_id=${programaId}`),
  getInscripcion: (id) =>
    axios.get(`${API_BASE_URL}/admin/wa-inscripciones.php?id=${id}`),
  createInscripcion: (data) =>
    axios.post(`${API_BASE_URL}/admin/wa-inscripciones.php`, data),
  importInscripciones: (programaId, contactos, fechaInicio) =>
    axios.post(`${API_BASE_URL}/admin/wa-inscripciones.php`, { action: 'import_bulk', programa_id: programaId, contactos, fecha_inicio: fechaInicio }),
  updateInscripcion: (id, data) =>
    axios.put(`${API_BASE_URL}/admin/wa-inscripciones.php`, { id, ...data }),
  deleteInscripcion: (id) =>
    axios.delete(`${API_BASE_URL}/admin/wa-inscripciones.php?id=${id}`),

  // Interacciones (read-only)
  getInteracciones: (params) =>
    axios.get(`${API_BASE_URL}/admin/wa-interacciones.php`, { params }),
};

// ============ Membership Service (Fase 11.8b) ============
export const membershipService = {
  getMisProyectos: () => axios.get(`${API_BASE_URL}/mis-proyectos.php`),
};

// ============ WA Client Dashboard Service (Fase 11.8) ============
export const waClientService = {
  getStatus: (proyectoId) =>
    axios.get(`${API_BASE_URL}/wa-dashboard.php`, { params: { action: 'status', proyecto_id: proyectoId || undefined } }),
  getProgramas: (proyectoId) =>
    axios.get(`${API_BASE_URL}/wa-dashboard.php`, { params: { action: 'programas', proyecto_id: proyectoId || undefined } }),
  getInscripciones: (programaId, proyectoId) =>
    axios.get(`${API_BASE_URL}/wa-dashboard.php`, { params: { action: 'inscripciones', programa_id: programaId, proyecto_id: proyectoId || undefined } }),
  getInteracciones: (params) =>
    axios.get(`${API_BASE_URL}/wa-dashboard.php`, { params: { action: 'interacciones', ...params } }),
  getMetricas: (proyectoId) =>
    axios.get(`${API_BASE_URL}/wa-dashboard.php`, { params: { action: 'metricas', proyecto_id: proyectoId || undefined } }),
  getRanking: (programaId, proyectoId) =>
    axios.get(`${API_BASE_URL}/wa-dashboard.php`, { params: { action: 'ranking', programa_id: programaId, proyecto_id: proyectoId || undefined } }),
  getAnalisisPreguntas: (programaId, proyectoId) =>
    axios.get(`${API_BASE_URL}/wa-dashboard.php`, { params: { action: 'analisis-preguntas', programa_id: programaId, proyecto_id: proyectoId || undefined } }),
  getDetalleEstudiante: (inscripcionId, proyectoId) =>
    axios.get(`${API_BASE_URL}/wa-dashboard.php`, { params: { action: 'detalle-estudiante', inscripcion_id: inscripcionId, proyecto_id: proyectoId || undefined } }),
  connectWhatsApp: (code, proyectoId) =>
    axios.post(`${API_BASE_URL}/wa-dashboard.php`, { action: 'connect-whatsapp', code, proyecto_id: proyectoId || undefined }),
  disconnectWhatsApp: (proyectoId) =>
    axios.post(`${API_BASE_URL}/wa-dashboard.php`, { action: 'disconnect-whatsapp', proyecto_id: proyectoId || undefined }),
};

// ============ Profile Service (Perfil de usuario) ============
export const profileService = {
  getProfile: () => axios.get(`${API_BASE_URL}/profile.php`),
  updateProfile: (data) => axios.put(`${API_BASE_URL}/profile.php`, data),
  changePassword: (actual, nueva) =>
    axios.post(`${API_BASE_URL}/profile.php?action=change_password`,
      { password_actual: actual, password_nueva: nueva }),
  uploadAvatar: (file) => {
    const fd = new FormData();
    fd.append('avatar', file);
    return axios.post(`${API_BASE_URL}/profile.php?action=upload_avatar`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' } });
  }
};

// ============ Mi Ranking Service (Ranking personal) ============
export const miRankingService = {
  getRanking: (documentoId) => axios.get(`${API_BASE_URL}/mi-ranking.php`, { params: { documento_id: documentoId } }),
  getRankingGlobal: () => axios.get(`${API_BASE_URL}/mi-ranking.php`)
};

// ============ Media Library Service (Fase 15) ============
export const mediaLibraryService = {
  // Media CRUD
  list: (params = {}) =>
    axios.get(`${API_BASE_URL}/admin/media-library.php`, { params }),
  getById: (id) =>
    axios.get(`${API_BASE_URL}/admin/media-library.php?id=${id}`),
  upload: (formData) =>
    axios.post(`${API_BASE_URL}/admin/media-library.php`, formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }),
  importVimeo: (data) =>
    axios.post(`${API_BASE_URL}/admin/media-library.php?action=import_vimeo`, data),
  update: (id, data) =>
    axios.put(`${API_BASE_URL}/admin/media-library.php`, { id, ...data }),
  delete: (id) =>
    axios.delete(`${API_BASE_URL}/admin/media-library.php?id=${id}`),
  importVimeoTranscript: (mediaId, idioma = 'es') =>
    axios.post(`${API_BASE_URL}/admin/media-library.php?action=import_vimeo_transcript`,
      { media_id: mediaId, idioma }),
};

export const mediaTagService = {
  list: (proyectoId) =>
    axios.get(`${API_BASE_URL}/admin/media-tags.php${proyectoId ? '?proyecto_id=' + proyectoId : ''}`),
  create: (data) =>
    axios.post(`${API_BASE_URL}/admin/media-tags.php`, data),
  update: (id, data) =>
    axios.put(`${API_BASE_URL}/admin/media-tags.php`, { id, ...data }),
  delete: (id) =>
    axios.delete(`${API_BASE_URL}/admin/media-tags.php?id=${id}`),
};

// ============ Documento Media Service (Fase 15.8) ============
export const documentoMediaService = {
  list: (documentoId) =>
    axios.get(`${API_BASE_URL}/admin/documento-media.php?documento_id=${documentoId}`),
  link: (documentoId, mediaId) =>
    axios.post(`${API_BASE_URL}/admin/documento-media.php`, { documento_id: documentoId, media_id: mediaId }),
  unlink: (id) =>
    axios.delete(`${API_BASE_URL}/admin/documento-media.php?id=${id}`),
};

// ============ Course Builder Service (Fase 15.4) ============
export const mentorCursoService = {
  // Cursos
  getEstructura: (documentoId) =>
    axios.get(`${API_BASE_URL}/admin/mentor-cursos.php?documento_id=${documentoId}`),
  getCurso: (id) =>
    axios.get(`${API_BASE_URL}/admin/mentor-cursos.php?id=${id}`),
  listCursos: (proyectoId) =>
    axios.get(`${API_BASE_URL}/admin/mentor-cursos.php${proyectoId ? '?proyecto_id=' + proyectoId : ''}`),
  createCurso: (data) =>
    axios.post(`${API_BASE_URL}/admin/mentor-cursos.php`, data),
  updateCurso: (id, data) =>
    axios.put(`${API_BASE_URL}/admin/mentor-cursos.php`, { id, ...data }),
  deleteCurso: (id) =>
    axios.delete(`${API_BASE_URL}/admin/mentor-cursos.php?id=${id}`),

  // Módulos
  createModulo: (data) =>
    axios.post(`${API_BASE_URL}/admin/mentor-modulos.php`, data),
  updateModulo: (id, data) =>
    axios.put(`${API_BASE_URL}/admin/mentor-modulos.php`, { id, ...data }),
  deleteModulo: (id) =>
    axios.delete(`${API_BASE_URL}/admin/mentor-modulos.php?id=${id}`),

  // Lecciones
  createLeccion: (data) =>
    axios.post(`${API_BASE_URL}/admin/mentor-lecciones.php`, data),
  updateLeccion: (id, data) =>
    axios.put(`${API_BASE_URL}/admin/mentor-lecciones.php`, { id, ...data }),
  deleteLeccion: (id) =>
    axios.delete(`${API_BASE_URL}/admin/mentor-lecciones.php?id=${id}`),

  // Items (contenido en lecciones)
  getItems: (leccionId) =>
    axios.get(`${API_BASE_URL}/admin/mentor-leccion-items.php?leccion_id=${leccionId}`),
  addItem: (data) =>
    axios.post(`${API_BASE_URL}/admin/mentor-leccion-items.php`, data),
  updateItem: (id, data) =>
    axios.put(`${API_BASE_URL}/admin/mentor-leccion-items.php`, { id, ...data }),
  removeItem: (id) =>
    axios.delete(`${API_BASE_URL}/admin/mentor-leccion-items.php?id=${id}`),

  // Reordenar (drag-and-drop)
  reorder: (tipo, orden, reparent) =>
    axios.put(`${API_BASE_URL}/admin/mentor-reorder.php`, { tipo, orden, reparent }),
};

export default {
  auth: authService,
  courses: courseService,
  lessons: lessonService,
  voice: voiceService,
  evaluations: evaluationService,
  questions: questionService,
  progress: progressService,
  admin: adminService,
  users: userService,
  certificates: certificadoService,
  completedCourses: completedCourseService,
  consulta: consultaService,
  attachments: attachmentService,
  transcriptions: transcriptionService,
  reto: retoService,  // Servicio de reto semanal
  notificaciones: notificacionService,
  mentor2: mentor2Service,
  seguimiento: seguimientoService,
  proyectos: proyectoService,
  convenios: convenioService,
  especialidades: especialidadService,
  paises: paisService
};
